import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeNl from '@angular/common/locales/nl';
import { Component, inject, signal } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { NavigationEnd, NavigationStart, RouterOutlet } from '@angular/router';

import {
  EMPTY,
  catchError,
  distinctUntilChanged,
  filter,
  pairwise,
  startWith,
  switchMap,
  take,
} from 'rxjs';

import { TaWebPushService } from '@ta/capacitor';
import { TaGoogleTagManagerService } from '@ta/core';
import { NotificationBoxComponent } from '@ta/notification';
import { PwaComponent } from '@ta/ui';
import { TA_AUTH_TOKEN, TaPermissionsService } from '@ta/user';

import { IncompleteProfileModal } from './features/base/modal/incomplete-profile/incomplete-profile';
import { BaseComponent } from './features/core/abstract/baseComponent';
import { NotificationService } from './services/notifications/notification.service';
import { AppTranslationApplication } from './services/translation.service';

registerLocaleData(localeFr);
registerLocaleData(localeEn);
registerLocaleData(localeNl);
registerLocaleData(localeDe);
@Component({
  selector: 'app-root',
  imports: [IncompleteProfileModal, NotificationBoxComponent, PwaComponent, RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App extends BaseComponent {
  private _authService = inject(TA_AUTH_TOKEN);
  private _bottomSheet = inject(MatBottomSheet);
  private _gtmService = inject(TaGoogleTagManagerService);
  private _permissionsService = inject(TaPermissionsService);
  private _webPush = inject(TaWebPushService);
  private _graphNotificationService = inject(NotificationService);

  protected readonly title = signal('bailo');
  public isIncompleteProfileOpen = signal(false);

  constructor() {
    super();

    AppTranslationApplication.getInstance();
    this._gtmService.addGtmToDom();
    this._authService.load();
    this._appTranslationService.init();
    this._bindWebPush();
    // `index.html` est figé en anglais : lecteurs d'écran et moteurs lisent la langue ici.
    this._registerSubscription(
      this._appTranslationService.onLangChange$
        .pipe(startWith(this._appTranslationService.getLanguage()))
        .subscribe((lang) => {
          if (lang) {
            document.documentElement.lang = lang;
          }
        }),
    );
    this._registerSubscription(
      this._authService.isLoading$
        .pipe(
          filter((loading) => !loading),
          take(1),
        )
        .subscribe(() => this._hideSplash()),
    );

    this._registerSubscription(
      this._router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          this._bottomSheet.dismiss();
          return;
        } else if (event instanceof NavigationEnd) {
          const gtmTag = {
            event: 'page',
            pageName: event.url,
          };

          this._gtmService.pushTag(gtmTag);
        }
      }),
    );
    this._registerSubscription(
      this._userService.userProfile.get$().subscribe({
        next: (user) => {
          if (
            user?.companyMembership?.id ||
            ((user?.tenant?.id || user?.owner?.id) && user?.firstname && user?.lastname)
          ) {
            this.isIncompleteProfileOpen.set(false);
            return;
          }
          this.isIncompleteProfileOpen.set(true);
        },
      }),
    );

    this._registerSubscription(
      this._userService.userProfile
        .get$()
        .pipe(
          startWith(null),
          pairwise(),
          filter(([prev, curr]) => !prev && !!curr),
          switchMap(([, user]) => {
            const currentLang = this._appTranslationService.getLanguage();
            if (user!.preferredLanguage === currentLang) {
              return EMPTY;
            }
            return this._userService
              .syncPreferredLanguage$(currentLang)
              .pipe(catchError(() => EMPTY));
          }),
        )
        .subscribe(),
    );
  }

  /**
   * Web Push : réenregistrer l'appareil à chaque connexion (abonnement perdu, clé VAPID tournée,
   * appareil passé à un autre compte), rafraîchir les listes quand un push arrive, suivre le clic
   * sur une notification quand cet onglet est déjà ouvert.
   */
  private _bindWebPush(): void {
    this._registerSubscription(
      this._permissionsService
        .canAccess$('', 'authenticated')
        .pipe(
          distinctUntilChanged(),
          filter((authenticated) => authenticated),
        )
        .subscribe(() => void this._webPush.refresh()),
    );

    this._registerSubscription(
      this._webPush.received$
        .pipe(
          switchMap(() =>
            this._graphNotificationService.refetchAll$().pipe(catchError(() => EMPTY)),
          ),
        )
        .subscribe(),
    );

    this._registerSubscription(
      this._webPush.navigate$.subscribe((href) => {
        const url = new URL(href, window.location.origin);
        this._router.navigateByUrl(`${url.pathname}${url.search}${url.hash}`);
      }),
    );
  }

  private _hideSplash(): void {
    const splash = document.getElementById('bailo-splash');
    if (!splash) {
      return;
    }
    splash.classList.add('splash-out');
    setTimeout(() => splash.remove(), 500);
  }
}
